import React, { useEffect, useState } from 'react'
import { Box, Divider, Tooltip, Typography } from '@mui/material'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { PatientService } from '../../services/patient.service'

export type KbStatus = 'MATCHED' | 'MISMATCHED' | 'UNKNOWN'

export interface KbVersionResponse {
  matched: boolean | null
  expected_version: number | null
  db_version: number | null
  cache_version: number | null
  commit: string | null
}

export const DOT_COLOR: Record<KbStatus, string> = {
  MATCHED: 'var(--color-kb-status-matched)',
  MISMATCHED: 'var(--color-kb-status-mismatched)',
  UNKNOWN: 'var(--color-kb-status-unknown)',
}

const UNKNOWN_RESPONSE: KbVersionResponse = {
  matched: null,
  expected_version: null,
  db_version: null,
  cache_version: null,
  commit: null,
}

/**
 * Spoken form of each status, for assistive tech. The dot must not rely on
 * colour alone, so the verdict is stated in words on the focusable wrapper.
 */
export const STATUS_LABEL: Record<KbStatus, string> = {
  MATCHED: 'Knowledge base in sync',
  MISMATCHED: 'Knowledge base out of sync',
  UNKNOWN: 'Knowledge base status unavailable',
}

/** Tri-state: anything that is not an explicit true/false is indeterminate. */
export const resolveStatus = (data: KbVersionResponse): KbStatus => {
  if (data.matched === true) return 'MATCHED'
  if (data.matched === false) return 'MISMATCHED'
  return 'UNKNOWN'
}

const KbStatusIndicator: React.FC = () => {
  // Starts indeterminate so the first paint degrades to "not known yet"
  // rather than flashing a false green before the response lands.
  const [data, setData] = useState<KbVersionResponse>(UNKNOWN_RESPONSE)

  useEffect(() => {
    let cancelled = false
    const patientService = new PatientService()
    patientService
      .getKBVersion()
      .then((res: any) => {
        if (!cancelled) setData(res?.data ?? UNKNOWN_RESPONSE)
      })
      .catch(() => {
        // Any failure is indeterminate, never a mismatch.
        if (!cancelled) setData(UNKNOWN_RESPONSE)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const status = resolveStatus(data)

  // When the codebase version itself could not be read, nothing is trustworthy.
  const totalFailure = data.expected_version === null || data.expected_version === undefined
  const hasDbVersion = data.db_version !== null && data.db_version !== undefined
  const hasCacheVersion = data.cache_version !== null && data.cache_version !== undefined

  const expectedText = totalFailure ? 'Unknown' : String(data.expected_version)
  const dbText = hasDbVersion
    ? String(data.db_version)
    : totalFailure
      ? 'Unknown'
      : 'Not recorded'
  const cacheText = hasCacheVersion ? String(data.cache_version) : 'Unknown'
  const commitText = data.commit ? data.commit.slice(0, 7) : 'Unknown'

  const tooltip = (
    <Box>
      <Typography variant="caption" component="div" sx={{ fontWeight: 700 }}>
        Knowledge-Base version
      </Typography>
      <Typography variant="caption" component="div">
        {`code: ${expectedText}`}
      </Typography>
      <Typography variant="caption" component="div">
        {`cache: ${cacheText}`}
      </Typography>
      <Typography variant="caption" component="div">
        {`database: ${dbText}`}
      </Typography>
      {/* currentColor, not a theme token: the tooltip bg is MUI's fixed dark
          grey in both themes, so a theme-relative divider color can vanish. */}
      <Divider sx={{ my: 0.5, borderColor: 'currentColor', opacity: 0.4 }} />
      <Typography variant="caption" component="div">
        {`Commit ID: ${commitText}`}
      </Typography>
    </Box>
  )

  // The wrapper carries focus and the accessible name; the icon itself cannot.
  // MUI's SvgIcon renders aria-hidden="true" and focusable="false" unless
  // titleAccess is given, so an aria-label on it is ignored by screen readers
  // and it can never receive keyboard focus — which would also make the
  // Tooltip mouse-only, since Tooltip opens on hover OR focus.
  return (
    <Tooltip title={tooltip} arrow>
      <Box
        component="span"
        role="img"
        tabIndex={0}
        aria-label={
          `${STATUS_LABEL[status]}. Knowledge-Base version — code: ${expectedText}. ` +
          `cache: ${cacheText}. database: ${dbText}. Commit ID: ${commitText}.`
        }
        data-testid="kb-status-dot"
        data-status={status}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          mr: 1,
          borderRadius: '50%',
          '&:focus-visible': {
            outline: '2px solid var(--color-primary)',
            outlineOffset: '2px',
          },
        }}
      >
        <FiberManualRecordIcon sx={{ fontSize: '0.75rem', color: DOT_COLOR[status] }} />
      </Box>
    </Tooltip>
  )
}

export default KbStatusIndicator
